/**
 * "Zoek in mail" bij het Feedback-blok van een v2/DEF-taak (Joris, 3 nov 2026): zoek in je eigen
 * mailbox (delegated Graph, alleen jouw mail), vink de klantmails aan en laat er een voorstel van
 * maken. Het voorstel komt in het tekstveld; jij past het aan en slaat het zelf op. Er wordt
 * niets automatisch opgeslagen of verstuurd, en de mailinhoud wordt niet bewaard.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';

import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';
import { getMailBody, searchMail } from '../../utils/graphMailSearch';

const GEDEMPT = '#64748b';

/** Beginzoekterm: projectnummer en klant, zoals de klant er zelf over mailt. */
export function zoekTermVoor(taak) {
  return [taak?.projectNumber, taak?.client].filter(Boolean).join(' ').trim();
}

const datumKort = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
};

const vanTekst = (m) => String(m?.from || '');

export default function FeedbackUitMail({ open, taak, onClose, onVoorstel }) {
  const [term, setTerm] = useState('');
  const [mails, setMails] = useState(null);
  const [gekozen, setGekozen] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTerm(zoekTermVoor(taak)); setMails(null); setGekozen([]); setFout(null);
  }, [open, taak?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoek = async () => {
    if (!term.trim()) return;
    setBezig(true); setFout(null);
    try {
      setMails(await searchMail(term.trim(), { top: 15 }));
      setGekozen([]);
    } catch (e) { setFout(`Mail zoeken lukt niet: ${e.message || e}`); }
    finally { setBezig(false); }
  };

  const wissel = (id) => setGekozen((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  const maakVoorstel = async () => {
    const lijst = (mails || []).filter((m) => gekozen.includes(m.id));
    if (!lijst.length) return;
    setBezig(true); setFout(null);
    try {
      const metTekst = [];
      for (const m of lijst) {
        // eslint-disable-next-line no-await-in-loop
        const body = await getMailBody(m.id);
        metTekst.push({ van: vanTekst(m), adres: m.fromAddress || '', datum: m.received || '', onderwerp: m.subject || '', tekst: String(body || m.preview || '') });
      }
      const r = await authFetch(`${BACKEND_URL}/Api/taak-feedback/${encodeURIComponent(taak.id)}/voorstel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mails: metTekst }),
      });
      const d = await leesJson(r, 'Voorstel maken');
      if (!d.punten?.length) {
        // Ook hier de inkorting noemen: de feedback kan in het niet-gelezen deel staan (Codex r5).
        const deels = (d.ingekort || []).length ? ` Let op: ${d.ingekort.join(', ')} is maar deels gelezen (te lang).` : '';
        setFout(`In deze mails staat geen concrete feedback. Kies andere mails.${deels}`);
        return;
      }
      onVoorstel(d.tekst, d.punten, d.ingekort || []);
      onClose();
    } catch (e) { setFout(e.message || 'Voorstel maken lukt niet'); }
    finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Feedback uit je mail</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: GEDEMPT, mb: 1.5 }}>
          Zoek in je eigen mailbox, vink de mails van de klant aan en laat er een voorstel van maken.
          Je past het voorstel daarna zelf aan en slaat het op.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField size="small" fullWidth label="Zoek (projectnummer, klant, onderwerp)" value={term}
            onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') zoek(); }} />
          <Button variant="outlined" onClick={zoek} disabled={bezig || !term.trim()} sx={{ textTransform: 'none' }}>Zoek</Button>
        </Box>
        {fout && <Alert severity="warning" sx={{ mb: 1 }}>{fout}</Alert>}
        {bezig && <CircularProgress size={18} />}
        {mails && !mails.length && !bezig && (
          <Typography variant="body2" sx={{ color: GEDEMPT }}>Niets gevonden. Probeer een andere zoekterm.</Typography>
        )}
        {(mails || []).map((m) => (
          <Box key={m.id} component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.5, cursor: 'pointer' }}>
            <Checkbox size="small" checked={gekozen.includes(m.id)} onChange={() => wissel(m.id)}
              inputProps={{ 'aria-label': m.subject || 'mail' }} sx={{ p: 0.5 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{m.subject || '(geen onderwerp)'}</Typography>
              <Typography sx={{ fontSize: 12, color: GEDEMPT }}>{vanTekst(m)} · {datumKort(m.received)}</Typography>
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={maakVoorstel} disabled={bezig || !gekozen.length} sx={{ textTransform: 'none' }}>
          Maak voorstel{gekozen.length ? ` (${gekozen.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
